/**
 * Onsite service appointments API.
 *
 * A technician visit to an existing client. Mirrors site inspection appointments
 * but carries a reason for the visit plus a list of tasks to accomplish while
 * on site. The detail page behaves like a task page: the technician checks off
 * tasks and adds notes and attachments.
 *
 * Lifecycle: Scheduled -> Completed | Void. `service_end_at` is null for a
 * single-day visit and set when the visit spans a date range.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export type OnsiteServiceStatus = 'Scheduled' | 'Completed' | 'Void';

export interface OnsiteServiceAssignee {
  user_id: number;
  username: string;
}

export interface OnsiteServiceTaskRow {
  id: number;
  appointment_id: number;
  description: string;
  is_complete: boolean;
  position: number;
  completed_at: string | null;
}

export interface OnsiteServiceRow {
  id: number;
  client_id: number | null;
  /** The linked client's Job Code / Deployment ID, e.g. A-000101. */
  job_code: string | null;
  business_name: string;
  business_address: string | null;
  point_of_contact: string | null;
  poc_email: string | null;
  poc_phone: string | null;
  service_at: string | null;
  service_end_at: string | null;
  reason: string | null;
  status: OnsiteServiceStatus;
  void_reason: string | null;
  assignees: OnsiteServiceAssignee[];
  tasks: OnsiteServiceTaskRow[];
  created_by_user_id: number | null;
  created_by_username: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OnsiteServiceListResponse {
  scheduled: OnsiteServiceRow[];
  completed: OnsiteServiceRow[];
  void: OnsiteServiceRow[];
}

export interface OnsiteServiceNoteRow {
  id: number;
  appointment_id: number;
  user_id: number | null;
  note: string;
  submitted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnsiteServiceAttachmentRow extends AttachmentVoidFields {
  id: number;
  appointment_id: number;
  original_file_name: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchOnsiteServiceAppointments(): Promise<OnsiteServiceListResponse> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments`);
  return jsonOrThrow(res, 'Failed to fetch onsite service appointments');
}

export async function fetchOnsiteServiceAppointment(id: number): Promise<OnsiteServiceRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}`);
  return jsonOrThrow(res, 'Failed to fetch appointment');
}

export interface CreateOnsiteServicePayload {
  user_id: number;
  client_id?: number | null;
  business_name: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  service_at?: string | null;
  service_end_at?: string | null;
  reason: string;
  assignee_user_ids?: number[];
  /** Tasks to accomplish during the visit, in the order entered. */
  tasks?: string[];
}

export async function createOnsiteServiceAppointment(
  payload: CreateOnsiteServicePayload
): Promise<OnsiteServiceRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to create appointment');
}

export interface UpdateOnsiteServicePayload {
  business_name?: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  service_at?: string | null;
  service_end_at?: string | null;
  reason?: string;
  status?: OnsiteServiceStatus;
  void_reason?: string | null;
  assignee_user_ids?: number[];
}

export async function updateOnsiteServiceAppointment(
  id: number,
  payload: UpdateOnsiteServicePayload
): Promise<OnsiteServiceRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update appointment');
}

export function completeOnsiteServiceAppointment(id: number): Promise<OnsiteServiceRow> {
  return updateOnsiteServiceAppointment(id, { status: 'Completed' });
}

export function voidOnsiteServiceAppointment(id: number, reason: string): Promise<OnsiteServiceRow> {
  return updateOnsiteServiceAppointment(id, { status: 'Void', void_reason: reason });
}

export function reopenOnsiteServiceAppointment(id: number): Promise<OnsiteServiceRow> {
  return updateOnsiteServiceAppointment(id, { status: 'Scheduled' });
}

// ── Visit tasks ──

export async function addOnsiteServiceTask(id: number, description: string): Promise<OnsiteServiceTaskRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description }),
  });
  const data = await jsonOrThrow<{ task: OnsiteServiceTaskRow }>(res, 'Failed to add task');
  return data.task;
}

export async function updateOnsiteServiceTask(
  id: number,
  taskId: number,
  payload: { description?: string; is_complete?: boolean }
): Promise<OnsiteServiceTaskRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await jsonOrThrow<{ task: OnsiteServiceTaskRow }>(res, 'Failed to update task');
  return data.task;
}

export async function deleteOnsiteServiceTask(id: number, taskId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/tasks/${taskId}`, {
    method: 'DELETE',
  });
  await jsonOrThrow(res, 'Failed to remove task');
}

// ── Notes ──

export async function fetchOnsiteServiceNotes(id: number): Promise<OnsiteServiceNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/notes`);
  const data = await jsonOrThrow<{ notes: OnsiteServiceNoteRow[] }>(res, 'Failed to load notes');
  return data.notes || [];
}

export async function addOnsiteServiceNote(id: number, note: string, userId: number): Promise<OnsiteServiceNoteRow> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId }),
  });
  return jsonOrThrow(res, 'Failed to add note');
}

// ── Attachments ──

export async function fetchOnsiteServiceAttachments(id: number): Promise<OnsiteServiceAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/attachments`);
  const data = await jsonOrThrow<{ attachments: OnsiteServiceAttachmentRow[] }>(res, 'Failed to load attachments');
  return data.attachments || [];
}

export async function uploadOnsiteServiceAttachment(
  id: number,
  file: File,
  userId?: number | null
): Promise<OnsiteServiceAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  if (userId != null && userId > 0) form.append('user_id', String(userId));
  const res = await fetch(`${API_BASE}/api/onsite-service-appointments/${id}/attachments`, {
    method: 'POST',
    body: form,
  });
  return jsonOrThrow(res, 'Failed to upload attachment');
}

export function onsiteServiceAttachmentDownloadUrl(id: number, attachmentId: number): string {
  return `${API_BASE}/api/onsite-service-appointments/${id}/attachments/${attachmentId}/download`;
}

/** Sets a file aside with a reason. The file stays on the appointment and can be restored. */
export function voidOnsiteServiceAttachment(
  id: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<OnsiteServiceAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/onsite-service-appointments/${id}/attachments/${attachmentId}`,
    reason,
    userId
  );
}

export function unvoidOnsiteServiceAttachment(
  id: number,
  attachmentId: number,
  userId?: number | null
): Promise<OnsiteServiceAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/onsite-service-appointments/${id}/attachments/${attachmentId}`,
    null,
    userId
  );
}
